import { useState, memo, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { Markdown, LottieContainer } from '@/sub-luca-plugin/compatible-components';
import { TypeReasoningContentState } from '@/sub-luca-plugin/types';
import classNames from 'classnames';
import './index.scss';
import { tCommon } from '@/sub-luca-plugin/i18n';

type TStatus = 'lucaThinking' | 'streaming' | 'analysising' | 'searching' | 'cardInserting';

export interface IReplyReminderRef {
    showLoading: (type: 'analysising' | 'lucaThinking' | 'cardInserting') => void;
    insertStreamText: (reasoningContent: string, content: string) => void;
    hide: () => void;
    simulateStreamReply: (content: string) => Promise<'finished'>;
    getCurrentStatus: () => TStatus | undefined;
}

interface IProps {
    onScrollToBottom: () => void;
}

const ReplyReminder = ({ onScrollToBottom }: IProps, ref: any) => {
    const statusRef = useRef<TStatus | undefined>();
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const simulateReplyTimer = useRef<ReturnType<typeof setInterval>>();
    const [status, setStatus] = useState<TStatus | undefined>();
    const [collapse, setCollapse] = useState(false);
    const [reasoningState, setReasoningState] = useState<TypeReasoningContentState>();
    // 推理内容
    const [reasoningContent, setReasoningContent] = useState('');
    // 回复内容
    const [content, setContent] = useState('');

    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    useImperativeHandle<any, IReplyReminderRef>(
        ref,
        () => ({
            showLoading: type => {
                setStatus(type);
                setContent('');
                setReasoningContent('');
                if (type === 'analysising') {
                    timer.current = setTimeout(() => setStatus('searching'), 1000);
                }
                onScrollToBottom();
            },
            insertStreamText: (reasoningContent, content) => {
                clearTimeout(timer.current);
                setStatus('streaming');
                if (content) {
                    setReasoningState(2);
                    setContent(content);
                } else if (reasoningContent) {
                    setReasoningState(0);
                    setReasoningContent(reasoningContent);
                }
                onScrollToBottom();
            },
            // 模拟流式回复
            simulateStreamReply: (content = '') => {
                clearTimeout(timer.current);
                setStatus('streaming');
                let oldText = '';
                return new Promise(resolve => {
                    let index = -1;
                    simulateReplyTimer.current = setInterval(() => {
                        setContent(() => {
                            index++;
                            if (index >= content.length - 1) {
                                resolve('finished');
                                clearInterval(simulateReplyTimer.current);
                                return '';
                            }
                            oldText += content[index];
                            return oldText;
                        });
                        onScrollToBottom();
                    }, 50);
                }).then(() => {
                    setStatus(undefined);
                    return 'finished';
                });
            },
            getCurrentStatus: () => statusRef.current,
            hide: () => {
                clearTimeout(timer.current);
                clearInterval(simulateReplyTimer.current);
                setStatus(undefined);
                setContent('');
                setReasoningContent('');
                setReasoningState(undefined);
                setCollapse(false);
            },
        }),
        []
    );

    switch (status) {
        case 'analysising':
        case 'searching':
            return (
                <div className="dialogue-area__analysising">
                    {status === 'analysising' ? tCommon.t('semanticAnalysis', '语义分析') : tCommon.t('knowledgeSearch', '知识搜索')}...
                </div>
            );
        case 'streaming':
        case 'lucaThinking':
            return (
                <div className="dialogue-area__stream-container">
                    {reasoningContent && (
                        <>
                            <div
                                className={classNames(
                                    'dialogue-area__reasoning-status',
                                    collapse && 'dialogue-area__reasoning-status--collapse'
                                )}
                                onClick={() => setCollapse(!collapse)}
                            >
                                {reasoningState === 0 && tCommon.t('thinking', '思考中...')}
                                {reasoningState === 2 && tCommon.t('deepThinkingCompleted', '已深度思考')}
                            </div>
                            <div
                                className={classNames(
                                    'dialogue-area__reasoning-content-container',
                                    collapse && 'dialogue-area__reasoning-content-container--collapse'
                                )}
                            >
                                <Markdown imgType="normal" content={reasoningContent} />
                            </div>
                        </>
                    )}
                    {(content || status === 'lucaThinking') && (
                        <div className="dialogue-area__stream-content-container">
                            <div
                                className={classNames(
                                    'dialogue-area__stream-content-markdown-container',
                                    status === 'lucaThinking' && 'dialogue-area__stream-content-markdown-container--loading'
                                )}
                            >
                                <Markdown
                                    content={
                                        status === 'lucaThinking'
                                            ? tCommon.t('lucaThinking', 'luca正在思考中...')
                                            : `${content} [](loading)`
                                    }
                                    status={status}
                                />
                                <LottieContainer
                                    className="dialogue-area__stream-content-markdown--loading-icon"
                                    type="purpleBallBouncing"
                                    excuteType="autoPlayByInit"
                                    loop={true}
                                />
                            </div>
                        </div>
                    )}
                </div>
            );
        case 'cardInserting':
            return (
                <div className="dialogue-area__card-inseting">
                    <LottieContainer width={80} height={30} type="purpleBallBouncing" excuteType="autoPlayByInit" loop={true} />
                </div>
            );
        default:
            return null;
    }
};

export default memo(forwardRef(ReplyReminder));
